<template>
    <div class="popup-backdrop" @click.self="closePopup">
        <div class="protocol-modal">
            <div class="protocol-modal-header">
                <span class="protocol-title">프로토콜 목록</span>
            </div>
            <div class="protocol-popup-page">
                <div class="protocol-search-row">
                    <div class="protocol-custom-select">
                        <select v-model="filterType">
                            <option value="all">전체</option>
                            <option value="basic">기본</option>
                            <option value="registered">등록</option>
                        </select>
                    </div>
                    <input type="text" placeholder="검색어 입력" v-model="searchTerm" @keyup.enter="searchProtocol"/>
                    <button class="protocol-btn" @click="resetSearch">초기화</button>
                    <button class="protocol-btn-primary" @click="onSearch">검색</button>
                </div>
            </div>
            <table class="protocol-table">
                <colgroup>
                    <col style="width: 8%" />
                    <col style="width: auto" />
                    <col style="width: auto" />
                    <col style="width: 20%" />
                </colgroup>
                <thead>
                    <tr>
                        <th></th>
                        <th>프로토콜명</th>
                        <th>등록일자</th>
                        <th>비고</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in protocolSummaryList" :key="item.name" :class="{ selected: selectedId === item.name }" @click="select(item.name)">
                        <td>
                            <input type="checkbox" :checked="selectedId === item.name" @change.stop="select(item.name)"/>
                        </td>
                        <td>{{ item.name }}</td>
                        <td>{{ item.createdAt }}</td>
                        <td>{{ item.type }}</td>
                    </tr>
                    <tr v-if="isLoaded && protocolSummaryList.length === 0">
                        <td colspan="4" style="text-align: center; padding: 20px;">프로토콜 목록이 없습니다.</td>
                    </tr>
                </tbody>
            </table>
            <div class="protocol-footer">
                <button class="protocol-btn" @click="onClose">취소</button>
                <button class="protocol-btn-primary" :disabled="!selectedId" @click="onApply">선택 적용</button>
            </div>
            <pagination
                :total-items="totalCount"
                :page-size="pageSize"
                v-model="currentPage"
                @page-change="changePage"
            />
        </div>
    </div>
</template>
<script setup>
    import { ref, computed,onMounted  } from 'vue';
    import api, {urls}                  from "@/api";
    import { useModal }                 from '@/composables/useModal';
    import Pagination                   from "@/components/common/Pagination.vue";
    import { useAppAlert }              from '@/composables/useAppAlert'

    const { showAlert, confirm } = useAppAlert()

    const { saveAndClose } = useModal()
    const emit = defineEmits(['close'])

    const protocolList      = ref([]);
    const selectedProtocol  = ref('');   // select 선택값
    const searchTerm        = ref('');   // 검색어 input
    const isLoaded          = ref(false);
    const protocolGroups    = ref({});
    const filterType        = ref('all');
    const selectedId        = ref(null);

    const protocolSummaryList = computed(() =>
        Object.entries(protocolGroups.value).map(([name, arr]) => {
            // key 예: "ProtocolB, 타입 r, 등록일자 Sat Aug 30 09:09:53 KST 2025"
            const parts         = name.split(', 등록일자 ');
            const protocolName  = parts[0].split(',')[0].trim(); // "ProtocolB"
            const createdAt     = parts[1] || '';                    // "Sat Aug 30 09:09:53 KST 2025"

            return {
                name: protocolName,
                count: arr.length,
                createdAt: createdAt,
                type: arr[0]?.protocolType === 'b' ? '기본'
                    : arr[0]?.protocolType === 'r' ? '등록'
                        : arr[0]?.protocolType || ''
            };
        })
    );

    const currentPage   = ref(1)
    const pageSize      = ref(10)
    const totalPages    = ref(1)
    const totalCount    = ref(0)

    const changePage = (page) => {
        searchProtocol(page)
    }

    const resetSearch = () => {
        filterType.value  = 'all';
        searchTerm.value  = '';
        currentPage.value = 1;
        searchProtocol(1);
    };

    function onSearch() {
        currentPage.value = 1;
        searchProtocol(currentPage.value);
    }

    function searchProtocol(page = 1) {
        const keyword = selectedProtocol.value || searchTerm.value;

        api.post(urls.protocol.protocolsearch, {protocolName: keyword, protocolType: filterType.value, page: currentPage.value,
            pageRow: pageSize.value})
            .then(res => {
                if (res.data && typeof res.data.protocolGroups === 'object') {
                    protocolGroups.value = res.data.protocolGroups;

                    // 필요하다면 protocolList 등 다른 상태도 함께 업데이트
                    protocolList.value = Object.entries(protocolGroups.value).map(([name, arr]) => ({
                        name,
                        count : arr.length,
                        type  : arr[0]?.protocolType || '',
                        raw   : arr
                    }));

                    totalCount.value  = res.data.totCnt || 0
                    totalPages.value  = Math.ceil(totalCount.value / pageSize.value);
                    currentPage.value = page;
                    isLoaded.value    = true;

                } else {
                    showAlert('프로토콜 데이터를 불러오지 못했습니다.');
                    protocolGroups.value = {};
                    protocolList.value   = [];
                    totalCount.value     = 0;
                    totalPages.value     = 1;
                    currentPage.value    = 1;
                    isLoaded.value       = false;
                }
            })
            .catch(err => {
                showAlert('검색 중 오류가 발생했습니다.');
            });
    }

    function select(id) {
        selectedId.value = id;
    }

    function onClose() {
        emit('close')
    }

    function onApply() {
        if (!selectedId.value) return;
        const selectedName = selectedId.value;

        api.post(urls.protocol.search, {protocolName : selectedName})
            .then(res => {
                protocolGroups.value = res.data.protocolGroups;

                const matchedGroups = Object.entries(protocolGroups.value).filter(([key, list]) => {
                    return key.startsWith(selectedName + ",");
                });
                const allProtocols = matchedGroups.flatMap(([key, list]) => list);
                saveAndClose('protocolLoaderModal', allProtocols);
            });
    }

    function closePopup() {
        emit('close')
    }

    onMounted(() => {
        // 초기에 모든 프로토콜 조회, 필터 타입을 'all'로 두고, searchTerm 없이 호출.
        searchProtocol();
    })
</script>